function getInventoryId(product) {
  const inventoryId = product?.inventory_id ?? product?.inventoryId;
  if (inventoryId == null || inventoryId === '') return '';
  return String(inventoryId);
}

function getOfferQuantityInCart(product) {
  const qty = Number(product?.qty_in_cart ?? product?.quantity_in_cart ?? 0);
  return Number.isFinite(qty) && qty > 0 ? qty : 0;
}

function buildCartProductMatchKey(product) {
  if (!product) return null;

  const inventoryId = getInventoryId(product);
  if (inventoryId) {
    return `inv:${inventoryId}`;
  }

  const productId = product.product_id ?? product.productId;
  const programId = product.program_id ?? product.programtypeId ?? product.programId;

  if (productId != null && productId !== '' && programId != null && programId !== '') {
    let scope = '';
    if (product.shopping_list_id) {
      scope = `:sbl:${product.shopping_list_id}`;
    } else if (product.hol_holiday_id) {
      scope = `:hol:${product.hol_holiday_id}`;
    } else if (product.farm_id != null && product.farm_id !== '') {
      scope = `:farm:${product.farm_id}`;
    }

    return `prod:${String(programId)}:${String(productId)}${scope}`;
  }

  return null;
}

export function buildCartQuantityLookup(cartState) {
  const map = {};
  if (!cartState || typeof cartState !== 'object') return map;

  const sections = Array.isArray(cartState) ? cartState : Object.values(cartState);
  sections.forEach((section) => {
    (section?.products || []).forEach((product) => {
      const qty = Number(product.quantity) || 0;
      const key = buildCartProductMatchKey(product);
      if (key) {
        map[key] = (map[key] || 0) + qty;
      }

      const inventoryId = product?.inventory_id ?? product?.inventoryId;
      if (inventoryId != null && inventoryId !== '') {
        const invKey = `inv:${inventoryId}`;
        map[invKey] = (map[invKey] || 0) + qty;
      }
    });
  });

  return map;
}

/** @deprecated Prefer getCartQuantityForProduct */
export function buildCartQuantityByInventory(cartState) {
  const map = {};
  if (!cartState || typeof cartState !== 'object') return map;

  const sections = Array.isArray(cartState) ? cartState : Object.values(cartState);
  sections.forEach((section) => {
    (section?.products || []).forEach((product) => {
      const inventoryId = product?.inventory_id;
      if (inventoryId == null || inventoryId === '') return;

      const key = String(inventoryId);
      map[key] = (map[key] || 0) + (Number(product.quantity) || 0);
    });
  });

  return map;
}

export function normalizeCartDeliveryDate(dateStr) {
  if (!dateStr) return '';
  const raw = String(dateStr).trim().split(' ')[0];
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, mm, dd, yyyy] = slashMatch;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  const dashMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (dashMatch) {
    const [, yyyy, mm, dd] = dashMatch;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  return raw;
}

function cartLineMatchesShopProduct(cartProduct, shopProduct) {
  const shopInventoryId = getInventoryId(shopProduct);
  const cartInventoryId = getInventoryId(cartProduct);

  // Same product can come from multiple vendors; inventory is the vendor row.
  if (shopInventoryId) {
    return Boolean(cartInventoryId) && shopInventoryId === cartInventoryId;
  }

  const targetKey = buildCartProductMatchKey(shopProduct);
  const cartKey = buildCartProductMatchKey(cartProduct);
  return Boolean(targetKey && cartKey === targetKey);
}

export function findCartLineItemForProduct(cartState, shopProduct, options = {}) {
  if (!cartState || typeof cartState !== 'object') return null;

  const normalizedDelivery = normalizeCartDeliveryDate(options.deliveryDate);
  const sections = Array.isArray(cartState) ? cartState : Object.values(cartState);
  let fallbackMatch = null;

  for (const section of sections) {
    const headerDate = normalizeCartDeliveryDate(section?.header?.delivery_date);
    const dateMatches = !normalizedDelivery || !headerDate || headerDate === normalizedDelivery;

    for (const cartProduct of section?.products || []) {
      if (!cartLineMatchesShopProduct(cartProduct, shopProduct)) {
        continue;
      }

      if (dateMatches) {
        return cartProduct;
      }

      if (!fallbackMatch) {
        fallbackMatch = cartProduct;
      }
    }
  }

  return fallbackMatch;
}

export function getCartQuantityForProduct(cartState, shopProduct, options = {}) {
  const lineItem = findCartLineItemForProduct(cartState, shopProduct, options);
  if (lineItem) {
    return Number(lineItem.quantity) || 0;
  }

  const fromOffer = getOfferQuantityInCart(shopProduct);
  if (fromOffer >= 1) {
    return fromOffer;
  }

  if (options.deliveryDate) {
    return 0;
  }

  const map = buildCartQuantityLookup(cartState);
  const key = buildCartProductMatchKey(shopProduct);
  if (key && map[key]) return map[key];

  const inventoryId = shopProduct?.inventory_id ?? shopProduct?.inventoryId;
  if (inventoryId != null && inventoryId !== '') {
    return map[`inv:${inventoryId}`] || 0;
  }

  return 0;
}

/** @deprecated Prefer getCartQuantityForProduct */
export function getCartQuantityByInventoryId(cartState, inventoryId) {
  if (inventoryId == null || inventoryId === '') return 0;
  return buildCartQuantityByInventory(cartState)[String(inventoryId)] || 0;
}

export function applyCartQuantitiesToProducts(products, cartState, options = {}) {
  if (!Array.isArray(products) || products.length === 0) return;

  products.forEach((product) => {
    const lineItem = findCartLineItemForProduct(cartState, product, options);
    if (lineItem) {
      const qtyInCart = Number(lineItem.quantity) || 0;
      product.quantity_in_cart = qtyInCart;
      product.qty_in_cart = qtyInCart;
      product.cart_item_id = lineItem.cart_item_id ?? null;
      return;
    }

    const existing = getOfferQuantityInCart(product);
    product.quantity_in_cart = existing;
    product.qty_in_cart = existing;
  });
}
